import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Download, Check, X, MessageSquareWarning, ShieldCheck, FileText } from "lucide-react";
import { PortalHeader, PortalEmpty, PortalCard } from "@/components/portal/PortalHeader";
import { getMyProject, getMyProposal, decideMyProposal, requestMyProposalChanges, generateMyProposalPdf } from "@/lib/portal/portal.functions";
import { SignaturePad } from "@/components/proposals/SignaturePad";

export const Route = createFileRoute("/_authenticated/portal/proposal")({
  component: ProposalPage,
});

function formatMoney(n: number | string | null | undefined) {
  const v = typeof n === "string" ? Number(n) : n;
  if (v == null || Number.isNaN(v)) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function StatusBadge({ status }: { status: string }) {
  const palette: Record<string, string> = {
    sent: "bg-blue-300/10 text-blue-200 border-blue-400/20",
    viewed: "bg-amber-300/10 text-amber-200 border-amber-400/20",
    approved: "bg-emerald-300/10 text-emerald-200 border-emerald-400/20",
    declined: "bg-rose-300/10 text-rose-200 border-rose-400/20",
    changes_requested: "bg-amber-300/10 text-amber-200 border-amber-400/20",
    expired: "bg-zinc-300/10 text-zinc-300 border-zinc-400/20",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] uppercase tracking-wider font-semibold ${palette[status] ?? palette.sent}`}>
      {status.replace("_", " ")}
    </span>
  );
}

function ProposalPage() {
  const qc = useQueryClient();
  const projectFn = useServerFn(getMyProject);
  const proposalFn = useServerFn(getMyProposal);
  const decideFn = useServerFn(decideMyProposal);
  const changeFn = useServerFn(requestMyProposalChanges);
  const pdfFn = useServerFn(generateMyProposalPdf);

  const projectQ = useQuery({ queryKey: ["portal", "project"], queryFn: () => projectFn() });
  const projectId = projectQ.data?.project?.id;
  const proposalQ = useQuery({
    queryKey: ["portal", "proposal", projectId],
    queryFn: () => proposalFn({ data: { projectId: projectId! } }),
    enabled: !!projectId,
  });

  const [decisionOpen, setDecisionOpen] = useState<null | "approve" | "decline" | "changes">(null);
  const [signName, setSignName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState("");
  const [changeMsg, setChangeMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  if (projectQ.isLoading || proposalQ.isLoading) {
    return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  }
  if (!projectId) return <PortalEmpty title="No project yet" description="Your project will appear once an admin invites you." />;

  const data = proposalQ.data;
  if (!data || !data.proposal) {
    return (
      <>
        <PortalHeader eyebrow="Proposal & Estimate" title="Proposal" />
        <PortalEmpty title="No proposal yet" description="Your formal proposal will appear here as soon as it's sent." />
      </>
    );
  }

  const p = data.proposal as any;
  const items = data.line_items as any[];
  const colors = data.colors as any[];
  const status: string = p.status;
  const finalized = ["approved", "declined", "expired", "void"].includes(status);

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      if (decisionOpen === "approve") {
        if (!signName.trim()) throw new Error("Please type your name to approve");
        if (!signature) throw new Error("Please draw your signature to approve");
        await decideFn({ data: { projectId, decision: "approve", name: signName.trim(), signature } });
      } else if (decisionOpen === "decline") {
        await decideFn({ data: { projectId, decision: "decline", name: signName.trim() || "Customer", reason: declineReason.trim() } });
      } else if (decisionOpen === "changes") {
        if (changeMsg.trim().length < 3) throw new Error("Please describe what you'd like changed");
        await changeFn({ data: { projectId, name: p.customer_name || "Customer", email: p.customer_email, message: changeMsg.trim() } });
      }
      await qc.invalidateQueries({ queryKey: ["portal", "proposal", projectId] });
      setDecisionOpen(null);
      setSignName(""); setSignature(null); setDeclineReason(""); setChangeMsg("");
    } catch (e: any) {
      setErr(e?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await pdfFn({ data: { projectId } });
      const bin = atob(res.base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: res.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = res.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setDownloading(false); }
  };

  return (
    <>
      <PortalHeader
        eyebrow={`Proposal ${p.proposal_number ?? ""}`}
        title={p.project_type ? `${p.project_type} project` : "Your proposal"}
        description={p.estimated_timeline ? `Estimated timeline: ${p.estimated_timeline}` : undefined}
        actions={
          <>
            <button onClick={download} disabled={downloading}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-xs text-zinc-200">
              {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />} PDF
            </button>
          </>
        }
      />

      <div className="flex items-center gap-3 mb-6">
        <StatusBadge status={status} />
        {p.first_viewed_at && <span className="text-xs text-zinc-500">Viewed {new Date(p.first_viewed_at).toLocaleDateString()}</span>}
        {p.expires_at && <span className="text-xs text-zinc-500">· Expires {new Date(p.expires_at).toLocaleDateString()}</span>}
      </div>

      {p.cover_image_url && (
        <PortalCard className="overflow-hidden mb-6">
          <img src={p.cover_image_url} alt="" className="w-full max-h-[340px] object-cover" />
        </PortalCard>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <PortalCard className="p-6 lg:col-span-2">
          <h3 className="font-serif text-lg text-white mb-3">Project scope</h3>
          <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{p.scope || "—"}</p>
        </PortalCard>
        <PortalCard className="p-6">
          <h3 className="font-serif text-lg text-white mb-4">Investment</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between text-zinc-400"><dt>Subtotal</dt><dd>{formatMoney(p.subtotal)}</dd></div>
            {Number(p.discount) > 0 && <div className="flex justify-between text-zinc-400"><dt>Discount</dt><dd>−{formatMoney(p.discount)}</dd></div>}
            <div className="flex justify-between text-white font-semibold border-t border-white/10 pt-2 mt-2"><dt>Total</dt><dd className="text-amber-300">{formatMoney(p.total)}</dd></div>
            {p.deposit_required && (
              <div className="flex justify-between text-zinc-400 pt-2 text-xs">
                <dt>Deposit ({p.deposit_percentage ?? ""}%)</dt><dd>{formatMoney(p.deposit_amount)}</dd>
              </div>
            )}
          </dl>
        </PortalCard>
      </div>

      <PortalCard className="p-6 mb-6">
        <h3 className="font-serif text-lg text-white mb-4 flex items-center gap-2"><FileText className="size-4 text-amber-300" /> Line items</h3>
        <div className="divide-y divide-white/5">
          {items.length === 0 && <p className="text-sm text-zinc-500">No itemized lines.</p>}
          {items.map((li) => (
            <div key={li.id} className="py-3 flex items-start justify-between gap-6">
              <div className="min-w-0">
                <p className="text-sm text-white font-medium">{li.title}</p>
                {li.description && <p className="text-xs text-zinc-400 mt-0.5">{li.description}</p>}
                {li.area_or_quantity != null && (
                  <p className="text-[11px] text-zinc-500 mt-1">{li.area_or_quantity} {li.unit ?? ""} × {formatMoney(li.unit_price)}</p>
                )}
              </div>
              <div className="text-sm text-amber-200 whitespace-nowrap">{formatMoney(li.total)}</div>
            </div>
          ))}
        </div>
      </PortalCard>

      {colors.length > 0 && (
        <PortalCard className="p-6 mb-6">
          <h3 className="font-serif text-lg text-white mb-4">Paint schedule</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {colors.map((c: any) => (
              <div key={c.id} className="flex items-center gap-3 rounded-xl border border-white/5 bg-black/30 p-3">
                <div className="size-12 rounded-lg shrink-0 border border-white/10" style={{ background: c.hex || "#222" }} />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-amber-300">{c.surface}</p>
                  <p className="text-sm text-white truncate">{c.color_name}</p>
                  <p className="text-[11px] text-zinc-500">SW {c.color_number} · {c.sheen ?? ""}</p>
                </div>
              </div>
            ))}
          </div>
        </PortalCard>
      )}

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {p.warranty && (
          <PortalCard className="p-6">
            <h3 className="font-serif text-lg text-white mb-2 flex items-center gap-2"><ShieldCheck className="size-4 text-amber-300" /> Warranty</h3>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{p.warranty}</p>
          </PortalCard>
        )}
        {p.terms && (
          <PortalCard className="p-6">
            <h3 className="font-serif text-lg text-white mb-2">Terms</h3>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{p.terms}</p>
          </PortalCard>
        )}
      </div>

      {/* Action bar */}
      {!finalized && (
        <div className="sticky bottom-4 z-30">
          <div className="rounded-2xl border border-amber-300/20 bg-gradient-to-br from-amber-300/[0.06] to-white/[0.02] backdrop-blur-xl p-4 flex flex-wrap items-center justify-between gap-3 shadow-[0_8px_40px_-12px_rgba(251,191,36,0.25)]">
            <p className="text-sm text-zinc-200">Ready to move forward? Approve, decline, or request changes.</p>
            <div className="flex gap-2">
              <button onClick={() => setDecisionOpen("changes")} className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-xs text-zinc-200 inline-flex items-center gap-1.5">
                <MessageSquareWarning className="size-3.5" /> Request changes
              </button>
              <button onClick={() => setDecisionOpen("decline")} className="rounded-full border border-rose-400/20 bg-rose-400/10 hover:bg-rose-400/20 px-4 py-2 text-xs text-rose-200 inline-flex items-center gap-1.5">
                <X className="size-3.5" /> Decline
              </button>
              <button onClick={() => setDecisionOpen("approve")} className="rounded-full bg-amber-300 hover:bg-amber-200 text-zinc-950 px-5 py-2 text-xs font-semibold inline-flex items-center gap-1.5">
                <Check className="size-3.5" /> Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {decisionOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4" onClick={() => !busy && setDecisionOpen(null)}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-white mb-1">
              {decisionOpen === "approve" ? "Approve proposal" : decisionOpen === "decline" ? "Decline proposal" : "Request changes"}
            </h3>
            <p className="text-xs text-zinc-500 mb-4">
              {decisionOpen === "approve" ? "Type your full name as an electronic signature." :
               decisionOpen === "decline" ? "Let us know briefly why (optional)." :
               "Tell us what you'd like adjusted."}
            </p>
            {decisionOpen === "approve" && (
              <div className="space-y-3">
                <input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Full name"
                  className="w-full text-base rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-white" />
                <div>
                  <p className="text-xs text-zinc-400 mb-1">Draw your signature</p>
                  <SignaturePad value={signature} onChange={setSignature} theme="dark" />
                </div>
              </div>
            )}
            {decisionOpen === "decline" && (
              <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} rows={4} placeholder="Reason (optional)"
                className="w-full text-base rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-white" />
            )}
            {decisionOpen === "changes" && (
              <textarea value={changeMsg} onChange={(e) => setChangeMsg(e.target.value)} rows={5} placeholder="What would you like to change?"
                className="w-full text-base rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-white" />
            )}
            {err && <p className="text-rose-300 text-xs mt-2">{err}</p>}
            <div className="flex justify-end gap-2 mt-5">
              <button disabled={busy} onClick={() => setDecisionOpen(null)} className="rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-300">Cancel</button>
              <button disabled={busy} onClick={submit} className="rounded-full bg-amber-300 hover:bg-amber-200 text-zinc-950 px-5 py-2 text-xs font-semibold inline-flex items-center gap-2">
                {busy && <Loader2 className="size-3 animate-spin" />} Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}